import { Box, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HeadCell } from '../../../../common/table/EnhancedTableHead';
import { useContext, useState } from 'react';
import { FeatureClassificationView } from '../../../../generated/ApiClient';
import { EnhancedTable } from '../../../../common/table/EnhancedTable';
import GroupFeatureDialog from './GrroupFeatureDialog';
import { ClassificationContext } from '../../context/ClassificationContext.types';

interface FeatureDefinitionRow {
  id: string;
  name: string;
  defaultValue: string;
  featureType: string;
  format?: string;
  isOverrideable: string;
  isInherited: string;
  sortOrder: string;
}

export function GroupFeatureList() {
  const { t } = useTranslation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<FeatureClassificationView | null>(null);

  const { refetchFeatures, featureClassifications, selectedGroup, isFeaturesLoading } =
    useContext(ClassificationContext);

  const handleAddFeature = () => {
    setEditingFeature(null);
    setIsDialogOpen(true);
  };

  const handleEditFeature = (feature: FeatureClassificationView) => {
    setEditingFeature(feature);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    refetchFeatures();
  };

  const headCells: readonly HeadCell<FeatureDefinitionRow>[] = [
    {
      id: 'name',
      disablePadding: false,
      label: t('classification.features.name'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'defaultValue',
      disablePadding: false,
      label: t('classification.groups.features.defaultValue'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'featureType',
      disablePadding: false,
      label: t('classification.features.type'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'format',
      disablePadding: false,
      label: t('classification.features.format'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'isInherited',
      disablePadding: false,
      label: t('classification.groups.features.isInherited'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'isOverrideable',
      disablePadding: false,
      label: t('classification.groups.features.isOverrideable'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'sortOrder',
      disablePadding: false,
      label: t('classification.groups.features.sortOrder'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'id',
      disablePadding: false,
      label: t('classification.features.id'),
      allowSorting: true,
      allowFiltering: true,
    },
  ];

  const handleRowClick = (e: React.MouseEvent<unknown>, id: string) => {
    e.stopPropagation();

    const feature = featureClassifications.find((x) => x.id === id);
    if (feature) handleEditFeature(feature);
  };

  const rows = featureClassifications.map((x) => ({
    ...x,
    format: x.format,
    featureType: t(`classification.features.types.${x.featureType}`),
    isInherited: x.isInherited ? 'ja' : 'nein',
    isOverrideable: x.isOverrideable ? 'ja' : 'nein', // TODO
    defaultValue: x.defaultValue ?? '',
    sortOrder: String(x.sortOrder),
  }));

  if (!selectedGroup) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h5">{t('classification.features.title')}</Typography>
        <Button variant="contained" onClick={handleAddFeature}>
          {t('classification.features.addFeature')}
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTable
          rows={rows}
          total={featureClassifications?.length}
          loading={isFeaturesLoading}
          headCells={headCells}
          hideCheckbox
          variant="default"
          onRowClick={handleRowClick}
          defaultOrderBy="sortOrder"
          defaultOrder="desc"
        />
      </Paper>

      <GroupFeatureDialog
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        featureClassification={editingFeature}
        onClose={handleDialogClose}
        groupId={selectedGroup.id}
      />
    </Box>
  );
}
